import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeader, formatDateDDMMYYYY } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { UserCheck, UserX, Users, Mail, Calendar } from "lucide-react";

const UserApproval = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-users`, { headers: getAuthHeader() });
      setPendingUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch pending users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (userId, approved) => {
    try {
      await axios.post(`${API}/admin/approve-user`, 
        { user_id: userId, approved }, 
        { headers: getAuthHeader() }
      );
      toast.success(approved ? "User approved successfully!" : "User denied and removed");
      fetchPendingUsers();
    } catch (error) {
      toast.error("Failed to process user approval");
    }
  };

  const formatDate = (dateString) => {
    return formatDateDDMMYYYY(dateString);
  };

  const getTeamColor = (team) => {
    const colors = {
      'Account': 'bg-blue-100 text-blue-700 border-blue-200',
      'Processing': 'bg-purple-100 text-purple-700 border-purple-200',
      'BackOffice': 'bg-green-100 text-green-700 border-green-200',
      'ITDept': 'bg-orange-100 text-orange-700 border-orange-200',
      'Partners': 'bg-pink-100 text-pink-700 border-pink-200'
    };
    return colors[team] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pending User Approvals
            </CardTitle>
            <CardDescription>
              Review and approve new team member registrations
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {pendingUsers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-slate-600 font-medium">All caught up!</p>
            <p className="text-sm text-slate-500 mt-1">No pending user approvals</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingUsers.map((user) => (
              <div 
                key={user.id}
                data-testid={`pending-user-${user.id}`}
                className="p-4 rounded-xl border border-slate-200 bg-slate-50 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold">
                        {user.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{user.name}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                        <Badge className={`${getTeamColor(user.team)} border`}>
                          {user.team}
                        </Badge>
                        <div className="flex items-center gap-1 text-slate-500 text-xs">
                          <Calendar className="w-3 h-3" />
                          Registered: {formatDate(user.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApproval(user.id, true)}
                      data-testid={`approve-user-${user.id}`}
                      className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                      size="sm"
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleApproval(user.id, false)}
                      data-testid={`deny-user-${user.id}`}
                      variant="destructive"
                      size="sm"
                    >
                      <UserX className="w-4 h-4 mr-1" />
                      Deny
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserApproval;
